import {
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Store } from '@ngxs/store';
import {
  Observable,
  catchError,
  switchMap,
  throwError,
  from,
  Subject,
  filter,
  take,
  shareReplay,
} from 'rxjs';
import { RefreshToken } from '../../store/users-store/user.actions';

// Shared state for token refresh coordination
let refreshTokenInProgress = false;
const refreshTokenSubject = new Subject<boolean>();

export const httpTokenInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const store = inject(Store);
  const token = localStorage.getItem('regen_access_token');

  if (req.url.includes('.s3.')) {
    return next(req);
  }

  // Local multi-tenant: send x-tenant-id so API hits the right DB without *.localhost hosts
  const headers: Record<string, string> = {};
  const tenant =
    (typeof localStorage !== 'undefined' && localStorage.getItem('dev_tenant')) || null;
  if (tenant) {
    headers['x-tenant-id'] = tenant;
  }

  // Skip adding Authorization header for auth requests
  if (req.url.includes('api/auth')) {
    return next(Object.keys(headers).length ? req.clone({ setHeaders: headers }) : req);
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (Object.keys(headers).length) {
    req = req.clone({ setHeaders: headers });
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        // If refresh is already in progress, wait for it to complete
        if (refreshTokenInProgress) {
          return refreshTokenSubject.pipe(
            filter((success) => success === true),
            take(1),
            switchMap(() => {
              const newToken = localStorage.getItem('regen_access_token');
              if (newToken) {
                const newRequest = req.clone({
                  setHeaders: { Authorization: `Bearer ${newToken}` },
                });
                return next(newRequest);
              }
              return throwError(() => error);
            }),
          );
        }

        // Start token refresh process
        refreshTokenInProgress = true;
        const refreshToken = localStorage.getItem('regen_refresh_token') ?? '';

        const refreshObservable = from(store.dispatch(new RefreshToken(refreshToken))).pipe(
          shareReplay(1), // Share the result among multiple subscribers
          switchMap(() => {
            const newToken = localStorage.getItem('regen_access_token');
            if (newToken) {
              refreshTokenInProgress = false;
              refreshTokenSubject.next(true);
              // Retry the original request with the new token
              const newRequest = req.clone({
                setHeaders: { Authorization: `Bearer ${newToken}` },
              });
              return next(newRequest);
            }
            refreshTokenInProgress = false;
            refreshTokenSubject.next(false);
            return throwError(() => error);
          }),
          catchError((refreshError) => {
            refreshTokenInProgress = false;
            refreshTokenSubject.next(false);
            return throwError(() => refreshError);
          }),
        );

        return refreshObservable;
      }
      return throwError(() => error);
    }),
  );
};
